/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useDeviceModelHint, useTheme } from 'hooks';
import { Camera, Checkbox } from 'assets';
import { makePost, queueAPIRequest, TranslateFunction, useTranslate, makeGet } from 'services';
import { Params } from '../capture-info.component';
import { PhotoSeries, WindshieldRepairReplaceDecision } from 'redux/photoseries/photoseries';
import { getPhotoSeries } from 'redux/photoseries/photoseries.selectors';
import { getDamages } from 'redux/damages/damages.selectors';
import { ImageTypeKeys, VIN_SUB_TYPES } from 'redux/workflows/workflows';
import { Button } from 'components/partials';
import { usePhotoSeriesStore } from 'components/pages/photoseries/store/root.hook';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { CameraMode, CaptureInfoImageType, CaptureInfoState, imageTypeKeys } from '../../../store/root';
import { captureInfoImageTypeContentPropFactory, hasLocation, instructionsStateFactory } from 'components/pages/photoseries/utils/photoseries.util';
import { getDamageCaptureProgress, selectUnsupportedBrowser } from 'redux/root.selectors';
import { useNativeCameraUpload } from 'components/pages/photoseries/hooks/use-native-camera-upload';
import { storeUseNativeCamera, updateWindhsieldRepairReplaceDecision } from 'redux/root.actions';
import { AjaxResponse } from 'rxjs/ajax';

const { vehicle_scan, damage_labelling, process_flow } = TRANSLATIONS_VALUES_KEYS;

const ImageTypeContentWrapper = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.7;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-template-rows: 7fr 1fr;
  overflow-x: hidden;
  overflow-y: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  padding: 2rem 1.75rem 3rem 1.75rem;
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  & .row-1 {
    width: 100%;

    & > h2 {
      font-size: 1.125rem;
      line-height: 1.125rem;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      font-weight: 700;

      .img-count {
        font-size: 0.75rem;
        line-height: 0.75rem;
        display: flex;
        align-items: end;
      }
    }

    & > p {
      font-size: 0.8125rem;
    }

    & .vehicle-captured-text {
      font-weight: 500;
      padding-bottom: 1rem;
    }
  }

  & .row-2 {
    margin: 0 auto;
    width: 100%;
  }
`;

const CheckboxWrapper = styled.div`
  background-color: transparent;
  border: 0rem;
  display: flex;
  align-items: center;
  padding-top: 0.8125rem;
  padding-bottom: 0.8125rem;
  text-align: start;
  font-weight: bold;
  font-size: 0.75rem;
  color: inherit;
  hyphens: auto;
  width: 100%;

  & svg {
    margin-right: 0.375rem;
    width: 10%;
  }

  & span {
    width: 90%;
  }
`;

export const ButtonContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  & > * {
    padding-right: 0.3125rem;
  }
`;

const CountdownButton = styled(Button)<{ progress: number }>`
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: rgba(255, 255, 255, 0.3) 100%;
    transition: width 0.1s ease-out;
    z-index: 1;
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

const GuidelineList = styled.ul<{ enableListStyle: boolean }>`
  padding: ${(props) => (props.enableListStyle ? '0.625rem' : '0')};

  & li {
    list-style: ${(props) => (props.enableListStyle ? 'initial' : 'none')};
  }
`;

type ImageTypeContentPropTypes = {
  imagesToBeCaptured: number | null;
  state: CaptureInfoState;
};

type FinishCaptureButtonProps = {
  disabled: boolean;
  finishCapturingInitiated: boolean;
  onFinishCapture: () => void;
  translate: TranslateFunction;
};

const FinishCaptureButton: React.FC<FinishCaptureButtonProps> = ({
  disabled,
  finishCapturingInitiated,
  onFinishCapture,
  translate
}) => {
  const initialCountdown = 5000; // 5 seconds in milliseconds
  const [countdown, setCountdown] = useState<number>(initialCountdown);
  const [userClicked, setUserClicked] = useState<boolean>(false); // Track if user clicked

  useEffect(() => {
    if (countdown > 0 && !userClicked) { // Only countdown if user hasn't clicked
      const timer = setTimeout(() => {
        setCountdown(countdown - 100); // Update every 100ms for smooth animation
      }, 100);
      return () => clearTimeout(timer);
    }

    // Auto-trigger when countdown reaches 0 (only if user hasn't clicked)
    if (countdown <= 0 && !userClicked) {
      onFinishCapture();
    }
  }, [countdown, onFinishCapture, userClicked]);

  // Handle user click
  const handleClick = () => {
    setUserClicked(true); // Mark that user clicked
    onFinishCapture(); // Immediately trigger finish capture
  };

  // Calculate progress percentage (0-100) - only show if user hasn't clicked
  const progress = userClicked ? 0 : ((initialCountdown - countdown) / initialCountdown) * 100;

  return (
    <CountdownButton
      testId="finishCapBtn"
      onClick={handleClick} // Use our custom handler
      animate
      disabled={disabled}
      isLoading={finishCapturingInitiated}
      progress={progress} // Will be 0 if user clicked
    >
      {translate(vehicle_scan.finish_btn)}
    </CountdownButton>
  );
};

export const ImageTypeContent: React.FC<ImageTypeContentPropTypes> = ({ imagesToBeCaptured, state }) => {
  const history = useHistory();
  const { styles } = useTheme();
  const { vehicleType } = useParams<Params>();
  const photoSeries: PhotoSeries = useSelector(getPhotoSeries);
  const { deviceNotRedmiNote } = useDeviceModelHint();
  const {
    dispatchCameraMode,
    dispatchSetInstructionsState,
    dispatchSetInstructionsCurrentImagesCaptured,
    dispatchSetCaptureInfoImagesCaptured,
    dispatchSetCaptureInfoSelectedImageType,
    dispatchSetCaptureInfoImageTypes,
    dispatchSetCaptureInfoIsAllImagesCaptured,
    captureImage: { cameraPhoto },
    captureInfo: { isAllImagesCaptured, imagesCaptured }
  } = usePhotoSeriesStore();

  const selectedImgType = state?.selectedImageType!;
  const selectedCustomImageTypeId = state?.selectedCustomImageTypeId;

  const imageTypeKey = imageTypeKeys.getStringKey()[selectedImgType];

  const _imgType = Number(selectedImgType);
  const _vehicleType = Number(vehicleType);
  const { selectedSubTypeIndex } = instructionsStateFactory(_imgType, _vehicleType, photoSeries).subType!;
  const imageSubType = instructionsStateFactory(_imgType, _vehicleType, photoSeries).subType?.subTypes[selectedSubTypeIndex!].subTypeDigit;
  const instructionsState = instructionsStateFactory(_imgType, _vehicleType, photoSeries, imageSubType);

  const [disabled, setDisabled] = useState<boolean>(false);
  const [finishCapturingInitiated, setFinishCapturingInitiated] = useState<boolean>(false);

  const isAdditionalImages = _imgType === ImageTypeKeys.Additional;
  const isManualDamageLabelling = photoSeries.manualDamageLabellingEnabled;
  const isCustomImageType = selectedCustomImageTypeId !== null;

  const unsupportedBrowser = useSelector(selectUnsupportedBrowser);
  const damageCaptureProgress = useSelector(getDamageCaptureProgress);
  const damages = useSelector(getDamages);
  const { translate, translateUnsafe } = useTranslate();
  const dispatch = useDispatch();

  const [finishInProgress, setFinishInProgress] = useState(false);
  const [wrrInProgress, setWrrInProgress] = useState(false);

  const imageTypeSettings = photoSeries?.imageTypeSettings
    .find((its) => its.imageType === selectedImgType && its.customImageTypeId === selectedCustomImageTypeId);

  const showDamageCaptureInstructions = isAdditionalImages &&
                                        isManualDamageLabelling &&
                                        (damages.length > 0 || photoSeries.automatedWindshieldCalculationInProgress) &&
                                        damageCaptureProgress?.current === 0 &&
                                        (imageTypeSettings?.enabledImageSubTypes ?? []).length > 0;

  const imageTypeCaptureInfo = imageTypeKey !== 'custom'
    ? state.imageTypes[imageTypeKey]
    : state.imageTypes.custom![selectedCustomImageTypeId!];

  const captureView = `/capture/${photoSeries.id}/image-type/${_imgType}/vehicle-type/${_vehicleType}/image-subType/${imageSubType}`;
  const { fileInput, mode } = useNativeCameraUpload(captureView);

  useEffect(() => {
    if (!photoSeries) return;
    if (photoSeries.smartScanNativeCameraEnabled && deviceNotRedmiNote) return;

    const imageTypeSettings = photoSeries.imageTypeSettings
      .find((its) => its.imageType === selectedImgType && its.customImageTypeId === selectedCustomImageTypeId);

    if (!imageTypeSettings) return;

    const allowNative = (imageTypeSettings.smartScanNativeCameraEnabled || unsupportedBrowser) && deviceNotRedmiNote;

    dispatch(storeUseNativeCamera(allowNative));
    dispatchCameraMode(allowNative ? CameraMode.Native : CameraMode.InBrowser);
  }, [
    photoSeries,
    selectedImgType,
    selectedCustomImageTypeId,
    dispatch,
    dispatchCameraMode,
    deviceNotRedmiNote,
    unsupportedBrowser
  ]);

  const detectWindshieldRepairability = useCallback(() => {
    const detectWindshieldRepairability = photoSeries.windshieldRepairReplaceDecisionEnabled &&
                                          photoSeries.windshieldRepairReplaceDecision === undefined &&
                                          !wrrInProgress;

    if (!detectWindshieldRepairability) return;

    setWrrInProgress(true);
    dispatch(updateWindhsieldRepairReplaceDecision(WindshieldRepairReplaceDecision.Calculating));
    queueAPIRequest(() => makeGet(`capture/${photoSeries.id}/detect-ws-repairability`))
      .then((response: AjaxResponse<any>) => {
        dispatch(updateWindhsieldRepairReplaceDecision(response.response));
      })
      .catch(() => {
        dispatch(updateWindhsieldRepairReplaceDecision(WindshieldRepairReplaceDecision.Uncertain));
      });
  }, [photoSeries, dispatch, wrrInProgress]);

  useEffect(() => {
    if (isAllImagesCaptured) {
      detectWindshieldRepairability();
    }
  }, [isAllImagesCaptured, detectWindshieldRepairability]);

  useEffect(() => {
    if (!finishCapturingInitiated) return;
    if (finishInProgress) return;

    // This is just a fallback incase WRR was not triggered, if it is triggered we ignore the execution inside
    // The point is that WRR must always be triggered before finish!
    detectWindshieldRepairability();

    setFinishInProgress(true);
    queueAPIRequest(() => makePost(`capture/${photoSeries.id}/completed`))
      .then(() => {
        cameraPhoto?.stopCamera();
        history.push(`/feedback/${photoSeries.id}`);
      });
  }, [finishCapturingInitiated, photoSeries, cameraPhoto, history, detectWindshieldRepairability, finishInProgress]);

  useEffect(() => {
    dispatchSetInstructionsState(instructionsState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_imgType, selectedSubTypeIndex]);

  const enabledSubTypesCount = useMemo(() => {
    if (![ImageTypeKeys.Exterior, ImageTypeKeys.Windshield].includes(selectedImgType)) return '';

    const _enabledSubTypesCount = photoSeries?.imageTypeSettings.find((v) => v.imageType === selectedImgType)?.enabledImageSubTypes?.length;

    return _enabledSubTypesCount ?? '';
  }, [photoSeries?.imageTypeSettings, selectedImgType]);

  const setImageTypeCheckboxChecked = useCallback(() => {
    if (!imageTypeCaptureInfo) return;
    if (disabled || finishCapturingInitiated) return;

    // Use requestAnimationFrame to avoid blocking DOM updates
    requestAnimationFrame(() => {
      const updatedImageType: CaptureInfoImageType = {
        ...imageTypeCaptureInfo,
        isCaptured: !imageTypeCaptureInfo.isCaptured
      } as CaptureInfoImageType;

      dispatchSetCaptureInfoImageTypes(imageTypeKey, selectedCustomImageTypeId, updatedImageType);
    });
  }, [imageTypeCaptureInfo, disabled, finishCapturingInitiated, imageTypeKey, selectedCustomImageTypeId, dispatchSetCaptureInfoImageTypes]);

  const renderGuidelines = useMemo(() => {
    const { captureInfoGuidelines } = captureInfoImageTypeContentPropFactory(selectedImgType);

    return (
      <GuidelineList key="guidelines-list" enableListStyle={imageSubType === VIN_SUB_TYPES.FIRST_VIN}>
        {captureInfoGuidelines.map((val, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={`guideline-${selectedImgType}-${index}`}>{val}</li>
        ))}
      </GuidelineList>
    );
  }, [imageSubType, selectedImgType]);

  const captureCustomImages = () => {
    const isAllCustomImagesCaptured = isCustomImageType && imageTypeCaptureInfo?.isCaptured;
    if (isAllCustomImagesCaptured) {
      const orderedImageTypeSettings = photoSeries.imageTypeSettings
        .filter((its) => its.enabled)
        .sort((its1, its2) => its1.order - its2.order);

      const currentImageTypeIndex = orderedImageTypeSettings
        .findIndex((its) => its.imageType === selectedImgType && its.customImageTypeId === selectedCustomImageTypeId);
      const lastImageTypeIndex = orderedImageTypeSettings.length - 1;

      // If custom image type is not last image type we forward user to the next image type
      if (currentImageTypeIndex !== lastImageTypeIndex) {
        const nextImageType = orderedImageTypeSettings[currentImageTypeIndex + 1].imageType;
        const nextCustomImageTypeId = orderedImageTypeSettings[currentImageTypeIndex + 1].customImageTypeId;

        dispatchSetCaptureInfoSelectedImageType(nextImageType, nextCustomImageTypeId);
        dispatchSetCaptureInfoImagesCaptured(0);
      // Otherwise all is captured
      } else {
        dispatchSetCaptureInfoIsAllImagesCaptured(true);
      }

      return;
    }

    if (mode === CameraMode.InBrowser) {
      history.push(captureView);
    }

    if (mode === CameraMode.Native) {
      fileInput?.click();
    }
  };

  const captureAdditionalImages = () => {
    if (imageTypeCaptureInfo!.isCaptured) {
      dispatchSetCaptureInfoIsAllImagesCaptured(true);
      return;
    }

    if (mode === CameraMode.InBrowser) {
      history.push(captureView);
    }

    if (mode === CameraMode.Native) {
      fileInput?.click();
    }
  };

  const capture = () => {
    setDisabled(true);

    const _hasLocation = hasLocation(selectedImgType, _vehicleType, photoSeries, imageSubType);
    const instructionsView = `/instructions/image-type/${selectedImgType}/vehicle-type/${vehicleType}/${_hasLocation ? 'location' : 'subType'}`;

    dispatchSetInstructionsCurrentImagesCaptured(0);
    history.push(instructionsView);
  };

  const handleFinishCapture = useCallback(() => {
    setDisabled(true);
    setFinishCapturingInitiated(true);
  }, []);

  const subTitleValues = {
    enabledSubTypesCount: String(enabledSubTypesCount)
  };

  const subTitle = isAllImagesCaptured
    ? translateUnsafe(vehicle_scan.vehicle_captured_sub_heading, subTitleValues)
    : !isCustomImageType
      ? translateUnsafe(imageTypeCaptureInfo!.subTitle, subTitleValues)
      : imageTypeCaptureInfo?.subTitle;

  return (
    <ImageTypeContentWrapper>
      <div className="row-1">
        <h2>
          {showDamageCaptureInstructions ? (
            translate(damage_labelling.capture_damage_title)
          ) : (
            <>
              <div>
                {isAllImagesCaptured
                  ? translate(vehicle_scan.vehicle_captured_heading)
                  : !isCustomImageType
                    ? translate(imageTypeCaptureInfo!.title)
                    : imageTypeCaptureInfo?.title}
              </div>
              {!isAllImagesCaptured && imagesToBeCaptured !== null && imagesToBeCaptured > 1 && (
                <span key="img-count-total" className="img-count">
                  {imagesToBeCaptured} &nbsp; {translate(vehicle_scan.images)}
                </span>
              )}
              {!isAllImagesCaptured && (isCustomImageType || isAdditionalImages) && (
                <span key="img-count-captured" className="img-count">
                  {imagesCaptured} &nbsp; {translate(vehicle_scan.images)}
                </span>
              )}
            </>
          )}
        </h2>
        <div className="content-container">
          {
            showDamageCaptureInstructions
              ? translateUnsafe(damage_labelling.capture_description)
              : (
                <>
                  <p>
                    { subTitle }
                  </p>
                  {!isAllImagesCaptured && renderGuidelines}
                </>
              )
          }
        </div>
      </div>

      <div className="row-2">
        {
          isAllImagesCaptured ? (
            <FinishCaptureButton
              key="finish-capture-section"
              disabled={disabled}
              finishCapturingInitiated={finishCapturingInitiated}
              onFinishCapture={handleFinishCapture}
              translate={translate}
            />
          ) : (
            <div key="capture-actions-section">
              {
                isCustomImageType && imagesCaptured === 0 && imageTypeSettings?.optional && (
                  <CheckboxWrapper key="no-images-checkbox" onClick={setImageTypeCheckboxChecked}>
                    <Checkbox checked={imageTypeCaptureInfo!.isCaptured} />
                      { imageTypeCaptureInfo?.noImagesCheckboxText }
                  </CheckboxWrapper>
                )
              }
              {
                isCustomImageType && imagesCaptured > 0 && (
                  <CheckboxWrapper key="all-captured-checkbox" onClick={setImageTypeCheckboxChecked}>
                    <Checkbox checked={imageTypeCaptureInfo!.isCaptured} />
                      { imageTypeCaptureInfo?.allCapturedCheckboxText }
                  </CheckboxWrapper>
                )
              }
              {
                isAdditionalImages && !showDamageCaptureInstructions && (
                  <CheckboxWrapper key="additional-images-checkbox" onClick={setImageTypeCheckboxChecked}>
                    <Checkbox checked={imageTypeCaptureInfo!.isCaptured} />
                      {translate(process_flow.additional.checkbox_text)}
                  </CheckboxWrapper>
                )
              }
              <Button
                key="capture-button"
                testId="startPsCapBtn"
                onClick={() => {
                  if (isCustomImageType) {
                    captureCustomImages();
                    return;
                  }

                  if (isAdditionalImages && !showDamageCaptureInstructions) {
                    captureAdditionalImages();
                    return;
                  }

                  capture();
                }}
                animate
                disabled={disabled || photoSeries.automatedWindshieldCalculationInProgress}
                isLoading={photoSeries.automatedWindshieldCalculationInProgress}
              >
                {!imageTypeCaptureInfo!.isCaptured && (
                  <span key="camera-icon" className="camera-icon">
                    <Camera fill={styles.colorPrimaryButtonText} /> &nbsp;
                  </span>
                )}
                {
                  showDamageCaptureInstructions
                    ? translate(damage_labelling.capture_button_text)
                    : isCustomImageType || (isAdditionalImages && !showDamageCaptureInstructions)
                      ? !imageTypeCaptureInfo!.isCaptured ? translate(vehicle_scan.capture) : translate(vehicle_scan.continue_btn)
                      : translate(imageTypeCaptureInfo!.buttonText)
                }
              </Button>
            </div>
          )
        }
      </div>
    </ImageTypeContentWrapper>
  );
};

export default ImageTypeContent;
